<template>
  <div class="dashboard-usage-chart">
    <div class="chart-header">
      <div>
        <h3 class="chart-title">{{ title }}</h3>
        <p class="chart-subtitle" v-if="subtitle">{{ subtitle }}</p>
      </div>
      <div class="chart-header-right">
        <div class="chart-view-modes" v-if="viewModes && viewModes.length > 1">
          <v-btn-toggle :value="activeViewMode" @change="$emit('update:activeViewMode', $event)" mandatory dense class="chart-toggle">
            <v-btn x-small :value="mode.value" v-for="mode in viewModes" :key="mode.value">{{ mode.label }}</v-btn>
          </v-btn-toggle>
        </div>
        <div class="chart-legend">
          <div class="legend-item" v-for="(s, i) in legendItems" :key="i">
            <span class="legend-swatch" :class="s.type" :style="{backgroundColor: s.color, borderColor: s.color}"></span>
            <span class="legend-label">{{ s.name }}</span>
          </div>
        </div>
      </div>
    </div>
    <highcharts v-if="chartOptions" :options="chartOptions" ref="chart" />
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardUsageChart',
  components: {
    highcharts: Chart,
  },
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    series: { type: Array, default: () => [] },
    yAxes: { type: Array, default: () => [] },
    height: { type: Number, default: 320 },
    viewModes: { type: Array, default: () => [] },
    activeViewMode: { type: String, default: '' },
  },

  computed: {
    legendItems() {
      return this.series.map(s => ({
        name: s.name,
        color: s.color,
        type: s.type === 'area' || s.type === 'areaspline' ? 'legend-swatch--area' : 'legend-swatch--line',
      }))
    },

    chartOptions() {
      if (!this.series || this.series.length === 0) return null

      const yAxisConfig = this.yAxes.length > 0
        ? this.yAxes.map((axis, i) => ({
          title: { text: axis.label || '', style: { color: axis.color || '#6b7280', fontSize: '11px' } },
          labels: { style: { color: axis.color || '#6b7280', fontSize: '10px' } },
          opposite: i > 0,
          gridLineColor: i === 0 ? '#f1f5f9' : 'transparent',
          min: 0,
        }))
        : [{ title: { text: '' }, gridLineColor: '#f1f5f9', min: 0 }]

      return {
        chart: {
          height: this.height,
          backgroundColor: 'transparent',
          style: { fontFamily: 'inherit' },
          spacingTop: 10,
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: {
          type: 'datetime',
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          gridLineWidth: 0,
          lineColor: '#e5e7eb',
        },
        yAxis: yAxisConfig,
        tooltip: {
          shared: true,
          crosshairs: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          shadow: true,
          style: { fontSize: '12px' },
        },
        plotOptions: {
          area: {
            fillOpacity: 0.15,
            lineWidth: 1.5,
            marker: { enabled: false, radius: 3, symbol: 'circle' },
          },
          areaspline: {
            fillOpacity: 0.15,
            lineWidth: 1.5,
            marker: { enabled: false },
          },
          line: {
            lineWidth: 2.5,
            marker: { enabled: false, radius: 3, symbol: 'circle' },
          },
          spline: {
            lineWidth: 2.5,
            marker: { enabled: false },
          },
          series: {
            animation: { duration: 800 },
          },
        },
        series: this.series.map((s, i) => ({
          ...s,
          yAxis: s.yAxis !== undefined ? s.yAxis : (i < yAxisConfig.length ? i : 0),
        })),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-usage-chart {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

.chart-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.chart-subtitle {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.chart-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.chart-toggle .v-btn {
  text-transform: none !important;
  font-size: 10px !important;
  letter-spacing: 0 !important;
  min-width: 40px !important;
}

.chart-legend {
  display: flex;
  align-items: center;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-swatch {
  display: inline-block;
  border-radius: 2px;

  &--area {
    width: 12px;
    height: 12px;
    opacity: 0.6;
    border: 1px solid;
  }

  &--line {
    width: 20px;
    height: 2px;
    border-radius: 1px;
  }
}

.legend-label {
  font-size: 12px;
  color: #4b5563;
  font-weight: 500;
}
</style>
